import React, { useEffect, useState } from "react";
import { api, asList, fileUrl, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { openPdf } from "@/lib/pdf";
import { Plus, ShoppingCart, Loader2, Check, ScanLine, Warehouse, AlertTriangle, FileDown } from "lucide-react";

/**
 * Procurement page — quick-lean UI over the /api/procurement/* backend. Watchmen use
 * only the "Inward" tab; Admin/Manager/Accounts see the full board.
 */
export function ProcurementPage() {
  const { user } = useAuth();
  const isWatchman = user?.role === "watchman";
  const [tab, setTab] = useState(isWatchman ? "inward" : "consumables");
  const [consumables, setConsumables] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [pos, setPOs] = useState([]);
  const [byDept, setByDept] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [threshold, setThreshold] = useState(5.0);
  const [openConsumable, setOpenConsumable] = useState(false);
  const [openVendor, setOpenVendor] = useState(false);
  const [openInward, setOpenInward] = useState(false);

  const load = async () => {
    try {
      if (!isWatchman) {
        const [c, v, p, b, a] = await Promise.all([
          api.get("/procurement/consumables"),
          api.get("/procurement/vendors").catch(() => ({ data: [] })),
          api.get("/procurement/pos").catch(() => ({ data: [] })),
          api.get("/procurement/analytics/by-department").catch(() => ({ data: [] })),
          api.get("/procurement/alerts").catch(() => ({ data: { items: [], threshold_pct: 5 } })),
        ]);
        setConsumables(asList(c.data)); setVendors(asList(v.data)); setPOs(asList(p.data)); setByDept(asList(b.data));
        const alertData = a.data || {};
        setAlerts(asList(alertData.items || alertData));
        setThreshold(Number(alertData.threshold_pct ?? 5));
      }
    } catch (err) { void err; }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  return (
    <div className="space-y-6" data-testid="procurement-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#4A5A50]">Procurement</div>
          <h1 className="font-serif text-3xl lg:text-4xl font-medium">{isWatchman ? "Goods Inward" : "Consumables & Purchases"}</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button className="rounded-full bg-[#0F5E39] hover:bg-[#083F22] text-white" onClick={() => setOpenInward(true)} data-testid="inward-btn"><ScanLine size={14} className="mr-1"/> Scan Invoice</Button>
          {!isWatchman && <Button variant="outline" className="rounded-full" onClick={() => setOpenConsumable(true)} data-testid="new-consumable-btn"><Plus size={14} className="mr-1"/> Consumable</Button>}
          {!isWatchman && <Button variant="outline" className="rounded-full" onClick={() => setOpenVendor(true)} data-testid="new-vendor-btn"><Plus size={14} className="mr-1"/> Vendor</Button>}
          {!isWatchman && (
            <Button
              variant="outline"
              className="rounded-full"
              onClick={async () => { try { await openPdf(`/api/reports/cost-attribution.pdf?days=30`); } catch { toast.error("PDF failed"); } }}
              data-testid="cost-attribution-pdf-btn"
            ><FileDown size={14} className="mr-1"/> Cost Attribution</Button>
          )}
        </div>
      </div>

      {!isWatchman && (
        <div className="flex gap-2 border-b border-[#E4DFCF] overflow-x-auto">
          {[["consumables", "Consumables"], ["vendors", "Vendors"], ["pos", "Purchase Orders"], ["alerts", `Price Alerts${alerts.length ? ` (${alerts.length})` : ""}`], ["analytics", "Leak Analysis"]].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} className={`px-3 py-2 text-sm whitespace-nowrap ${tab === k ? "border-b-2 border-[#0F5E39] text-[#0F1E15] font-medium" : "text-[#4A5A50]"}`} data-testid={`proc-tab-${k}`}>{l}</button>
          ))}
        </div>
      )}

      {!isWatchman && tab === "consumables" && <ConsumablesTable rows={consumables}/>}
      {!isWatchman && tab === "vendors" && <VendorsTable rows={vendors}/>}
      {!isWatchman && tab === "pos" && <POsTable rows={pos}/>}
      {!isWatchman && tab === "alerts" && <PriceAlerts rows={alerts} threshold={threshold} onAck={load} onThresholdChange={load}/>}
      {!isWatchman && tab === "analytics" && <LeakAnalysis rows={byDept}/>}
      {isWatchman && (
        <Card className="border-[#E4DFCF] rounded-xl"><CardContent className="py-12 text-center">
          <Warehouse size={40} className="text-[#C9A24B] mx-auto mb-3" strokeWidth={1.5}/>
          <div className="font-serif text-xl">Ready at the gate</div>
          <p className="text-sm text-[#4A5A50] mt-2">Tap <strong>Scan Invoice</strong> when goods arrive. AI reads the invoice and matches it to any open Purchase Order.</p>
        </CardContent></Card>
      )}

      <ConsumableDialog open={openConsumable} onClose={() => setOpenConsumable(false)} onDone={() => { setOpenConsumable(false); load(); }}/>
      <VendorDialog open={openVendor} onClose={() => setOpenVendor(false)} onDone={() => { setOpenVendor(false); load(); }}/>
      <InwardDialog open={openInward} onClose={() => setOpenInward(false)} onDone={() => { setOpenInward(false); load(); }} vendors={vendors} consumables={consumables}/>
    </div>
  );
}

function ConsumablesTable({ rows }) {
  if (rows.length === 0) return <Empty label="No consumables yet"/>;
  return (
    <Card className="border-[#E4DFCF] rounded-xl"><CardContent className="p-0">
      <table className="w-full text-sm">
        <thead className="text-[10px] uppercase text-[#4A5A50] text-left">
          <tr className="border-b border-[#E4DFCF]"><th className="p-3">Name</th><th>Unit</th><th>Stock</th><th>Reorder</th><th>Price</th></tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.id} className="border-b border-[#E4DFCF]/60" data-testid={`consumable-row-${r.id}`}>
              <td className="p-3">{r.name}<div className="text-[10px] text-[#4A5A50]">{r.category || ""}</div></td>
              <td>{r.unit}</td>
              <td className={r.reorder_level && r.stock_on_hand < r.reorder_level ? "text-red-600 font-medium" : ""}>{r.stock_on_hand}</td>
              <td>{r.reorder_level ?? "—"}</td>
              <td>{r.current_price != null ? `₹ ${Number(r.current_price).toLocaleString("en-IN")}` : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </CardContent></Card>
  );
}
function VendorsTable({ rows }) {
  if (rows.length === 0) return <Empty label="No vendors yet"/>;
  return (
    <Card className="border-[#E4DFCF] rounded-xl"><CardContent className="p-0">
      <ul className="divide-y divide-[#E4DFCF]">
        {rows.map(v => (
          <li key={v.id} className="p-4 flex justify-between" data-testid={`vendor-row-${v.id}`}>
            <div><div className="font-medium">{v.name}</div><div className="text-xs text-[#4A5A50]">{v.contact_person || ""} {v.phone ? "· " + v.phone : ""}</div></div>
            <div className="text-xs text-[#4A5A50]">{v.gstin || ""}</div>
          </li>
        ))}
      </ul>
    </CardContent></Card>
  );
}
function POsTable({ rows }) {
  if (rows.length === 0) return <Empty label="No POs raised yet"/>;
  return (
    <Card className="border-[#E4DFCF] rounded-xl"><CardContent className="p-0">
      <ul className="divide-y divide-[#E4DFCF]">
        {rows.map(p => (
          <li key={p.id} className="p-4 flex justify-between items-center" data-testid={`po-row-${p.id}`}>
            <div>
              <div className="font-medium">{p.po_number} · {p.vendor_name}</div>
              <div className="text-xs text-[#4A5A50]">{p.items?.length || 0} items · ₹ {Math.round(p.subtotal || 0).toLocaleString("en-IN")}</div>
            </div>
            <span className="text-[10px] uppercase tracking-widest bg-[#FCF7EA] text-[#7A6329] px-2 py-1 rounded-full">{p.status}</span>
          </li>
        ))}
      </ul>
    </CardContent></Card>
  );
}
function LeakAnalysis({ rows }) {
  if (rows.length === 0) return <Empty label="No consumption logged yet"/>;
  const max = Math.max(...rows.map(r => r.spend || 0)) || 1;
  return (
    <Card className="border-[#E4DFCF] rounded-xl"><CardContent className="p-6">
      <div className="text-[10px] uppercase tracking-widest text-[#4A5A50] mb-3">Consumable spend by department</div>
      <ul className="space-y-3">
        {rows.map(r => (
          <li key={r.department || "unknown"} data-testid={`spend-${r.department}`}>
            <div className="flex justify-between text-sm mb-1"><span>{r.department || "Unknown"}</span><span className="font-medium">₹ {Math.round(r.spend).toLocaleString("en-IN")}</span></div>
            <div className="w-full h-2 bg-[#F1EDDF] rounded-full overflow-hidden"><div className="h-full bg-[#0F5E39]" style={{ width: `${(r.spend / max) * 100}%` }}/></div>
          </li>
        ))}
      </ul>
    </CardContent></Card>
  );
}
function Empty({ label }) { return <div className="text-center py-16 text-[#4A5A50] text-sm">{label}</div>; }

function PriceAlerts({ rows, threshold, onAck, onThresholdChange }) {
  const [saving, setSaving] = useState(false);
  const [thresholdInput, setThresholdInput] = useState(String(threshold ?? 5));
  useEffect(() => { setThresholdInput(String(threshold ?? 5)); }, [threshold]);

  const saveThreshold = async () => {
    const v = Number(thresholdInput);
    if (Number.isNaN(v) || v < 0 || v > 100) { toast.error("Threshold must be 0-100"); return; }
    setSaving(true);
    try {
      await api.patch("/procurement/settings", { price_alert_threshold_pct: v });
      toast.success(`Alert threshold set to ${v}%`);
      onThresholdChange?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setSaving(false); }
  };

  const ack = async (id) => {
    try { await api.post(`/procurement/alerts/${id}/ack`); toast.success("Acknowledged"); onAck?.(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };

  return (
    <Card className="border-[#E4DFCF] rounded-xl" data-testid="price-alerts-panel"><CardContent className="p-4 space-y-4">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#4A5A50]">Vendor price-increase alerts</div>
          <div className="text-xs text-[#4A5A50] mt-1">Fires when a vendor's new rate exceeds the last-recorded rate by more than the threshold. In-app notification + email (via Encharge).</div>
        </div>
        <div className="flex items-end gap-2">
          <div><Label className="text-[10px] uppercase tracking-widest">Threshold %</Label>
            <Input type="number" step="0.1" min="0" max="100" value={thresholdInput}
              onChange={(e) => setThresholdInput(e.target.value)} className="w-24" data-testid="alert-threshold-input"/></div>
          <Button onClick={saveThreshold} disabled={saving} className="rounded-full bg-[#0F5E39] hover:bg-[#083F22] text-white" data-testid="alert-threshold-save">
            {saving ? <Loader2 size={14} className="animate-spin mr-2"/> : <Check size={14} className="mr-1"/>} Save
          </Button>
        </div>
      </div>
      {rows.length === 0 ? (
        <div className="py-10 text-center text-sm text-[#4A5A50]">
          <AlertTriangle size={24} className="text-[#C9A24B] mx-auto mb-2" strokeWidth={1.5}/>
          No open price alerts. You'll see any vendor rate jump above <strong>{threshold}%</strong> here.
        </div>
      ) : (
        <ul className="divide-y divide-[#E4DFCF]">
          {rows.map(a => (
            <li key={a.id} className="py-3 flex items-center justify-between gap-3" data-testid={`price-alert-${a.id}`}>
              <div className="min-w-0">
                <div className="font-medium truncate">{a.consumable_name || a.consumable_id} · {a.vendor_name || "Vendor"}</div>
                <div className="text-xs text-[#4A5A50]">
                  ₹{Number(a.old_price).toLocaleString("en-IN")} → ₹{Number(a.new_price).toLocaleString("en-IN")}
                  {a.reference ? ` · ${a.reference}` : ""} · {a.source}
                </div>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                <span className="text-red-700 font-medium text-sm">↑ {Number(a.increase_percent).toFixed(2)}%</span>
                <Button size="sm" variant="outline" className="rounded-full" onClick={() => ack(a.id)} data-testid={`price-alert-ack-${a.id}`}>Acknowledge</Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </CardContent></Card>
  );
}

function ConsumableDialog({ open, onClose, onDone }) {
  const [f, setF] = useState({ name: "", category: "", unit: "pcs", current_price: "", reorder_level: "", stock_on_hand: 0 });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => { if (open) { setF({ name: "", category: "", unit: "pcs", current_price: "", reorder_level: "", stock_on_hand: 0 }); setError(""); } }, [open]);
  const submit = async () => {
    if (!f.name.trim()) { setError("Name required"); return; }
    setSaving(true);
    try {
      const payload = { ...f };
      payload.current_price = payload.current_price === "" ? null : Number(payload.current_price);
      payload.reorder_level = payload.reorder_level === "" ? null : Number(payload.reorder_level);
      payload.stock_on_hand = Number(payload.stock_on_hand) || 0;
      await api.post("/procurement/consumables", payload);
      toast.success("Consumable added"); onDone();
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="consumable-dialog"><DialogHeader><DialogTitle className="font-serif text-2xl">New Consumable</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label>Name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="consumable-name"/></div>
          <div><Label>Category</Label><Input value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })}/></div>
          <div><Label>Unit</Label><Input value={f.unit} onChange={(e) => setF({ ...f, unit: e.target.value })} placeholder="kg/g/pcs"/></div>
          <div><Label>Current price</Label><Input type="number" step="0.01" value={f.current_price} onChange={(e) => setF({ ...f, current_price: e.target.value })}/></div>
          <div><Label>Reorder level</Label><Input type="number" step="0.01" value={f.reorder_level} onChange={(e) => setF({ ...f, reorder_level: e.target.value })}/></div>
          <div className="col-span-2"><Label>Stock on hand</Label><Input type="number" step="0.01" value={f.stock_on_hand} onChange={(e) => setF({ ...f, stock_on_hand: e.target.value })}/></div>
        </div>
        {error && <div className="text-sm text-red-600">{error}</div>}
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={submit} disabled={saving} className="bg-[#0F5E39] hover:bg-[#083F22] text-white" data-testid="consumable-submit-btn">{saving ? <Loader2 size={14} className="animate-spin mr-2"/> : <Check size={14} className="mr-1"/>} Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function VendorDialog({ open, onClose, onDone }) {
  const [f, setF] = useState({ name: "", contact_person: "", phone: "", email: "", gstin: "", address: "" });
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setF({ name: "", contact_person: "", phone: "", email: "", gstin: "", address: "" }); }, [open]);
  const submit = async () => {
    if (!f.name.trim()) { toast.error("Name required"); return; }
    setSaving(true);
    try { await api.post("/procurement/vendors", f); toast.success("Vendor added"); onDone(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent><DialogHeader><DialogTitle className="font-serif text-2xl">New Vendor</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label>Name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="vendor-name"/></div>
          <div><Label>Contact</Label><Input value={f.contact_person} onChange={(e) => setF({ ...f, contact_person: e.target.value })}/></div>
          <div><Label>Phone</Label><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })}/></div>
          <div><Label>Email</Label><Input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })}/></div>
          <div><Label>GSTIN</Label><Input value={f.gstin} onChange={(e) => setF({ ...f, gstin: e.target.value })}/></div>
          <div className="col-span-2"><Label>Address</Label><Input value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })}/></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={submit} disabled={saving} className="bg-[#0F5E39] hover:bg-[#083F22] text-white" data-testid="vendor-submit-btn">{saving ? <Loader2 size={14} className="animate-spin mr-2"/> : <Check size={14} className="mr-1"/>} Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InwardDialog({ open, onClose, onDone, vendors, consumables }) {
  const [phase, setPhase] = useState("upload"); // upload | scanning | review
  const [parsed, setParsed] = useState(null);
  const [imageUrls, setImageUrls] = useState([]);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [vendorId, setVendorId] = useState("");
  const [items, setItems] = useState([]);
  const [invoiceNo, setInvoiceNo] = useState("");
  const [invoiceDate, setInvoiceDate] = useState("");

  useEffect(() => { if (open) { setPhase("upload"); setParsed(null); setImageUrls([]); setError(""); setVendorId(""); setItems([]); setInvoiceNo(""); setInvoiceDate(""); } }, [open]);

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []); if (!files.length) return;
    setPhase("scanning"); setError("");
    try {
      const fd = new FormData(); files.forEach(f => fd.append("files", f));
      const { data } = await api.post("/procurement/inward/scan", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setParsed(data.parsed); setImageUrls(data.image_urls || []);
      setInvoiceNo(data.parsed?.invoice_number || "");
      setInvoiceDate(data.parsed?.invoice_date || "");
      const scanned = Array.isArray(data.parsed?.items) ? data.parsed.items : [];
      setItems(scanned.map(x => ({ consumable_id: "", consumable_name: x.name || "", unit: x.unit || "pcs", quantity: Number(x.quantity) || 0, rate: Number(x.rate) || 0 })));
      setPhase("review");
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail) || "Scan failed"); setPhase("upload"); }
  };

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {
        vendor_id: vendorId || null,
        vendor_name: parsed?.vendor_name || null,
        invoice_number: invoiceNo || null,
        invoice_date: invoiceDate ? new Date(invoiceDate).toISOString() : null,
        items: items.filter(i => i.consumable_id).map(i => ({ ...i, quantity: Number(i.quantity), rate: Number(i.rate) })),
        invoice_image_urls: imageUrls,
        ocr_source: parsed?._source || "manual",
      };
      if (!payload.items.length) { setError("Map at least one line to a consumable"); setSaving(false); return; }
      await api.post("/procurement/inward/receipt", payload);
      toast.success("Goods receipt saved");
      onDone();
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="inward-dialog">
        <DialogHeader><DialogTitle className="font-serif text-2xl">Goods Inward</DialogTitle></DialogHeader>
        {phase === "upload" && (
          <div className="py-8 text-center space-y-3">
            <div className="mx-auto w-20 h-20 rounded-full bg-[#F1EDDF] flex items-center justify-center"><ScanLine size={32} className="text-[#0F5E39]"/></div>
            <div className="font-serif text-xl">Scan invoice</div>
            <p className="text-sm text-[#4A5A50]">Capture all pages of the invoice. AI will extract vendor and line items.</p>
            <label className="inline-flex items-center gap-2 rounded-full bg-[#0F5E39] hover:bg-[#083F22] text-white px-6 py-3 cursor-pointer text-sm"><ScanLine size={16}/> Choose photos
              <input type="file" multiple accept="image/*" capture="environment" className="hidden" onChange={onFiles} data-testid="inward-file-input"/>
            </label>
            {error && <div className="text-sm text-red-600" data-testid="inward-error">{error}</div>}
          </div>
        )}
        {phase === "scanning" && (
          <div className="py-12 text-center"><Loader2 className="animate-spin text-[#0F5E39] mx-auto mb-3" size={28}/><div className="font-serif text-lg">Reading invoice…</div></div>
        )}
        {phase === "review" && (
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Vendor</Label><Select value={vendorId || undefined} onValueChange={setVendorId}><SelectTrigger data-testid="inward-vendor"><SelectValue placeholder={parsed?.vendor_name || "Pick"}/></SelectTrigger><SelectContent>{vendors.map(v => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent></Select></div>
              <div><Label>Invoice no.</Label><Input value={invoiceNo} onChange={(e) => setInvoiceNo(e.target.value)}/></div>
              <div><Label>Invoice date</Label><Input type="date" value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)}/></div>
            </div>
            <div className="text-[10px] uppercase tracking-widest text-[#4A5A50]">Line items (map each to a consumable)</div>
            <div className="space-y-2 max-h-72 overflow-auto">
              {items.map((it, i) => (
                <div key={i} className="grid grid-cols-5 gap-2 items-end">
                  <div className="col-span-2">
                    <div className="text-xs text-[#4A5A50] truncate">{it.consumable_name}</div>
                    <Select value={it.consumable_id || undefined} onValueChange={(v) => { const arr = [...items]; arr[i].consumable_id = v; setItems(arr); }}>
                      <SelectTrigger data-testid={`inward-map-${i}`}><SelectValue placeholder="Map to consumable"/></SelectTrigger>
                      <SelectContent className="max-h-64">{consumables.map(c => <SelectItem key={c.id} value={c.id}>{c.name} ({c.unit})</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-[10px]">Qty</Label><Input type="number" step="0.001" value={it.quantity} onChange={(e) => { const arr = [...items]; arr[i].quantity = e.target.value; setItems(arr); }}/></div>
                  <div><Label className="text-[10px]">Rate</Label><Input type="number" step="0.01" value={it.rate} onChange={(e) => { const arr = [...items]; arr[i].rate = e.target.value; setItems(arr); }}/></div>
                  <div className="text-right text-sm pt-4">₹ {((Number(it.quantity) || 0) * (Number(it.rate) || 0)).toFixed(2)}</div>
                </div>
              ))}
              {items.length === 0 && <div className="text-sm text-[#4A5A50] text-center py-4">No items extracted. Add manually below.</div>}
            </div>
            <Button variant="outline" size="sm" onClick={() => setItems([...items, { consumable_id: "", consumable_name: "", unit: "pcs", quantity: 0, rate: 0 }])}>+ Add line</Button>
            {error && <div className="text-sm text-red-600">{error}</div>}
            <DialogFooter>
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button onClick={submit} disabled={saving} className="bg-[#0F5E39] hover:bg-[#083F22] text-white" data-testid="inward-submit-btn">{saving ? <Loader2 size={14} className="animate-spin mr-2"/> : <Check size={14} className="mr-1"/>} Save receipt</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
